import moment from "moment";
import { Box, IconButton, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useNavigate } from "react-router-dom";
import { ExamAttempt, ExamResultResponse } from "../../../api/interfaces/Exam";
import { getExamHistoryById } from "../../../api/services/exam";
import { handleApiError } from "../../../utils/errorHandler";
import { pushNotification } from "../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { NotificationTypes } from "../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { formatPercentage } from "../../../utils/formatPercentage";

interface ExamHistoryDetailsProps {
    examCodeId: string;
    examCodeName: string;
    exam: ExamResultResponse | null;
}

const TableSkeleton = ({ rows = 5 }: { rows?: number }) => (
    <TableContainer
        component={Paper}
        sx={{
            mt: 2,
            boxShadow: "none",
            backgroundColor: "transparent",
        }}
    >
        <Table>
            <TableHead>
                <TableRow>
                    {[...Array(5)].map((_, i) => (
                        <TableCell key={i}>
                            <Skeleton variant="text" height={16} />
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {[...Array(rows)].map((_, r) => (
                    <TableRow key={r}>
                        {[...Array(5)].map((_, c) => (
                            <TableCell key={c}>
                                <Skeleton variant="text" height={14} />
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

const ExamHistoryDetails: React.FC<ExamHistoryDetailsProps> = ({ examCodeId, examCodeName, exam }) => {
    const navigate = useNavigate();
    const [examAttempts, setExamAttempts] = useState<ExamAttempt[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        const run = async () => {
            if (!examCodeId && !exam) return;
            setLoading(true);
            try {
                if (exam) {
                    const getExamHistory = await getExamHistoryById(exam._id);
                    setExamAttempts(getExamHistory.data.attempts);
                }
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
            } finally {
                setLoading(false);
            }

        }
        run();
    }, [examCodeId, exam]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 400, color: '#5840BA', mb: 1 }}>Exam Name:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, color: '#373737' }}>{exam?.name}</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 400, color: '#5840BA', mb: 1 }}>Exam Code:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, color: '#373737' }}>{examCodeName}</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 400, color: '#5840BA', mb: 1 }}>Duration:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, color: '#373737' }}>{exam?.timer} min{exam && exam.timer !== 1 && 's'}</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 400, color: '#5840BA', mb: 1 }}>Correct Answers / Total Items:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, color: '#373737' }}>
                    {exam?.highestScore === null ? "TBA" : exam?.highestScore} / {exam?.numberOfQuestions}
                </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 400, color: '#5840BA', mb: 1 }}>Highest Grade:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, color: '#373737' }}>{formatPercentage(exam?.grade ?? null)}</Typography>
            </Box>
            {loading ? <TableSkeleton />
                : examCodeId && examAttempts.length === 0 ? (
                    // No results for selected code
                    <>
                        {/* <Box
                            component="img"
                            src={EMPTY_STATE_ILLUSTRATION_URL}
                            alt="No Exams Illustration"
                            sx={{
                                alignSelf: "center",
                                width: 200,
                                height: 200,
                                mb: 2,
                                opacity: 0.85,
                            }}
                        /> */}
                        <Typography
                            variant="body2"
                            sx={{
                                alignSelf: "center",
                                color: "#818181",
                                fontWeight: 500,
                                fontSize: "12px",
                                mt: 0.5,
                            }}
                        >
                            No data to display
                        </Typography>
                    </>
                ) : (
                    <>
                        {examAttempts && (
                            <>
                                <TableContainer
                                    component={Paper}
                                    sx={{
                                        mt: 2,
                                        boxShadow: 'none',
                                        backgroundColor: 'transparent',
                                        overflowX: "auto",
                                    }}
                                >
                                    <Table
                                        sx={{
                                            borderCollapse: "separate",
                                            borderSpacing: 0,
                                            minWidth: "max-content",
                                            tableLayout: "auto",
                                        }}
                                    >
                                        <TableHead
                                            sx={{
                                                backgroundColor: "#5840BA",
                                                display: { sm: "table-header-group" },
                                                "& th": {
                                                    height: "31px",
                                                    padding: "6px 12px",
                                                    lineHeight: "19px",
                                                },
                                            }}
                                        >
                                            <TableRow sx={{ '& th': { border: 'none' } }}>
                                                <TableCell
                                                    sx={{
                                                        color: "#fff",
                                                        fontWeight: 500,
                                                        fontSize: "12px",
                                                        whiteSpace: "nowrap",
                                                        wordBreak: "keep-all",
                                                    }}
                                                >
                                                    Attempt No.
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        color: "#fff",
                                                        fontWeight: 500,
                                                        fontSize: "12px",
                                                        whiteSpace: "nowrap",
                                                        wordBreak: "keep-all",
                                                    }}
                                                >
                                                    Date Taken
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        color: "#fff",
                                                        fontWeight: 500,
                                                        fontSize: "12px",
                                                        whiteSpace: "nowrap",
                                                        wordBreak: "keep-all",
                                                    }}
                                                >
                                                    Correct Answers / Total Items
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        color: "#fff",
                                                        fontWeight: 500,
                                                        fontSize: "12px",
                                                        whiteSpace: "nowrap",
                                                        wordBreak: "keep-all",
                                                    }}
                                                >
                                                    Grade
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        color: "#fff",
                                                        fontWeight: 500,
                                                        fontSize: "12px",
                                                        whiteSpace: "nowrap",
                                                        wordBreak: "keep-all",
                                                    }}
                                                >
                                                    Exam Link
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {examAttempts.map((attempt: ExamAttempt, i: number) => (
                                                <TableRow
                                                    sx={{
                                                        backgroundColor: i % 2 === 0 ? "#F7F8FD" : "#FFFFFF",
                                                        "& td": {
                                                            border: "none",
                                                            verticalAlign: "middle",
                                                            textAlign: "center",
                                                            py: { xs: 1, sm: 1.5 },
                                                        },
                                                    }}
                                                >
                                                    <TableCell>
                                                        <Typography
                                                            noWrap
                                                            sx={{
                                                                fontSize: "12px"
                                                            }}
                                                        >
                                                            {attempt.attemptNumber}
                                                        </Typography>
                                                    </TableCell>

                                                    <TableCell>
                                                        <Typography
                                                            noWrap
                                                            sx={{
                                                                fontSize: "12px"
                                                            }}
                                                        >
                                                            {moment(attempt.dateTaken).format("DD-MM-YYYY")}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            noWrap
                                                            sx={{
                                                                fontSize: "12px"
                                                            }}
                                                        >
                                                            {attempt.score === null ? "TBA" : attempt.score} / {exam?.numberOfQuestions}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            noWrap
                                                            sx={{
                                                                fontSize: "12px",
                                                            }}
                                                        >
                                                            {formatPercentage(attempt.grade)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ textAlignLast: "center" }}>
                                                        <IconButton
                                                            sx={{
                                                                borderRadius: "4px",
                                                                border: "1px solid #5840BA",
                                                                color: "#5840BA",
                                                                textTransform: "none",
                                                                fontSize: "14px",
                                                                fontWeight: 400,
                                                                backgroundColor: "#FFFFFF",
                                                                "&:hover": {
                                                                    backgroundColor: "#F7F5FF",
                                                                    borderColor: "#5840BA",
                                                                },
                                                            }}
                                                            onClick={() => navigate(`/exam/${attempt._id}/view`)}
                                                        >
                                                            <DescriptionOutlinedIcon sx={{ fontSize: "18px" }} />
                                                        </IconButton >
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </>
                        )}
                    </>
                )}
        </Box>
    );
};

export default ExamHistoryDetails;